<template>
    <common-modal width="extra-large" @close="emitEvent({ name: 'close' })">
        <div v-if="showPricingDetails" class="pricing-cards">
            <pricing-cards @upgrade="showPricingDetails = !showPricingDetails"></pricing-cards>
        </div>
        <div v-else class="billing-information">
            <div class="billing-information--side">
                <common-content-spacer height="small">
                    <p>{{ $t('pages.practice.billing.plan_type') }}</p>
                    <div class="billing-information--side--plan">
                        <common-page-header
                            :heading="$t('pages.billing.billing.premium')"
                            class="referral-details__heading"
                            contentSpacing="medium"
                            size="large"
                        ></common-page-header>
                        <div>
                            <p v-if="isAnnual" class="billing-form--info">
                                <span>${{ yearlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.year') }}
                            </p>
                            <p v-else class="billing-form--info">
                                <span>${{ monthlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.month') }}
                            </p>
                        </div>
                    </div>
                    <div class="billing-information--side--price">
                        <div class="billing-form-type">
                            <span>{{ $t('pages.billing.billing.billing_form.monthly') }}</span>
                            <switch-input
                                :value="isAnnual"
                                spacing="space-between"
                                :showBlueCircle="true"
                                @change="changeIsAnnual($event)"
                            ></switch-input>
                            <span>{{ $t('pages.billing.billing.billing_form.annual') }}</span>
                        </div>

                        <p class="billing-information--side--price__save">
                            <span class="billing-information--side--price__save--text">
                                {{ $t('pages.practice.billing.you_save') }}
                                <span>${{ 12 * monthlyPrice - yearlyPrice }} {{ $t('pages.billing.billing.on_annual') }}</span>
                            </span>
                        </p>
                    </div>
                    <common-tabs :tabs="tabs" :activeTab="tabs[0].name">
                        <template v-slot:features>
                            <billing-features-list :featuresList="featuresList" :isHeaderCenter="false"></billing-features-list>
                        </template>
                        <template v-slot:details>
                            <price-explaination v-if="numberOfItems"></price-explaination>
                        </template>
                    </common-tabs>
                </common-content-spacer>
            </div>
            <div class="billing-information--main">
                <billing-payment-form
                    :monthlyPrice="monthlyPrice"
                    :yearlyPrice="yearlyPrice"
                    :isAlreadySubscribed="isAlreadySubscribed"
                    :isAnnual="isAnnual"
                    @billing-address-saved="saveBillingAddress"
                ></billing-payment-form>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'
    import BillingPaymentForm from 'components/page-elements/practice/billing/BillingPaymentForm'
    import PracticesAPI from 'api/PracticesAPI'
    import SwitchInput from 'components/form-inputs/SwitchInput'
    import PriceExplaination from 'components/page-elements/practice/billing/PriceExplaination'

    export default {
        mixins: [FormMixin],
        components: { BillingFeaturesList, BillingPaymentForm, SwitchInput, PriceExplaination, PricingCards },

        props: {
            isAlreadySubscribed: { type: Boolean, default: false },
        },

        data() {
            return {
                isSending: false,
                featuresList: [],
                monthlyPrice: null,
                yearlyPrice: null,
                isAnnual: true,
                numberOfItems: null,
                showPricingDetails: true,
            }
        },

        computed: {
            tabs() {
                return [
                    {
                        name: 'features',
                        displayName: this.$t('pages.billing.billing.features'),
                    },
                    {
                        name: 'details',
                        displayName: this.$t('pages.billing.billing.billing_details'),
                    },
                ]
            },
        },

        methods: {
            saveBillingAddress() {
                this.emitEvent({ name: 'close' })
                this.$auth.forceUpdatePractice()
            },

            getEstimatedPrice() {
                PracticesAPI.getEstimatedPrice().then((response) => {
                    this.monthlyPrice = response.monthlyPrice
                    this.yearlyPrice = response.yearlyPrice
                    this.numberOfItems = response.numberOfItems
                })
            },

            changeIsAnnual() {
                this.isAnnual = !this.isAnnual
            },
        },

        mounted() {
            this.getEstimatedPrice()
            this.featuresList = [
                this.$t('pages.practice.billing.payment_form_list.item_1'),
                this.$t('pages.practice.billing.payment_form_list.item_2'),
                this.$t('pages.practice.billing.payment_form_list.item_3'),
                this.$t('pages.practice.billing.payment_form_list.item_4'),
                this.$t('pages.practice.billing.payment_form_list.item_5'),
                this.$t('pages.practice.billing.payment_form_list.item_6'),
                this.$t('pages.practice.billing.payment_form_list.item_7'),
                this.$t('pages.practice.billing.payment_form_list.item_8'),
                this.$t('pages.practice.billing.payment_form_list.item_9'),
                this.$t('pages.practice.billing.payment_form_list.item_10'),
            ]
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .billing-default-plan {
        &--info {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 700;
            font-size: 18px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;
        }

        &--accept {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 500;
            font-size: 14px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;

            .icon {
                margin-left: 15px;
            }
        }
    }
    .billing-form-type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 12px;
        max-width: 260px;
        .form-field {
            margin: 0 10px;
        }
    }
    .billing-information {
        display: flex;

        @include breakpoint(tablet) {
            display: block;
            max-height: 700px;
        }

        &--side {
            @include breakpoint(tablet) {
                min-width: 100%;
                padding-right: 0;
            }
            background: $color--bg-dashboard;
            border-radius: 8px;
            padding: 10px;
            min-width: 371px;

            .tab-wrapper {
                .tab {
                    @include breakpoint(tablet) {
                        width: 100%;
                    }
                }
            }

            &--plan {
                display: flex;
                justify-content: space-between;

                p {
                    font-weight: 600;
                    color: $color-black;
                    margin-top: 5px;
                }
            }

            &--price {
                &__value {
                    font-weight: 600;
                    font-size: 20px;
                    line-height: 30px;
                    letter-spacing: 0.325px;
                    color: $color--accent-3;
                }

                &__save {
                    font-family: 'Poppins';
                    font-style: normal;
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 160%;
                    color: $color--txt-base-6;
                    text-align: right;
                    margin-top: 15px;

                    &--text {
                        border: 1px solid $color--txt-base-3;
                        padding: 7px 14px;
                        border-radius: 30px;
                        box-shadow: rgba(17, 17, 26, 0.1) 0px 4px 16px, rgba(17, 17, 26, 0.1) 0px 8px 24px,
                            rgba(17, 17, 26, 0.1) 0px 16px 56px;
                    }

                    span {
                        font-weight: 600;
                    }
                }
            }
        }

        &--main {
            @include breakpoint(tablet) {
                padding: 0;
            }

            width: 100%;
        }
    }
</style>
